import type { TransferData } from '../../constants'
import { cleanText, firstText, normalizeDate, normalizeDescriptionText, parseDescriptionFromHtml, readMeta } from '../../utils/parser'
import type { JsonObject } from '../../utils/jsonld'
import { readBookJsonLd, stringifyJsonLdValue } from '../../utils/jsonld'

function readDetailValue (labels: string[]): string {
  for (const labelElement of Array.from(document.querySelectorAll<HTMLElement>('dt, th'))) {
    const label = cleanText(labelElement.textContent || '').replace(/[:：]$/, '').toLowerCase()
    if (!labels.some((candidate) => candidate.toLowerCase() === label)) continue

    const valueElement = labelElement.nextElementSibling as HTMLElement | null
    const value = cleanText(valueElement?.textContent || '')
    if (value) return value
  }

  const candidates = Array.from(document.querySelectorAll<HTMLElement>('li, tr, dl, div, p'))

  for (const row of candidates) {
    const text = cleanText(row.textContent || '')
    if (!text || text.length > 260) continue

    for (const label of labels) {
      const escapedLabel = label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      const regex = new RegExp(`^${escapedLabel}\\s*[:：]\\s*(.+)$`, 'i')
      const match = text.match(regex)
      if (match?.[1]) return cleanText(match[1])
    }
  }

  return ''
}

function parseTitle (bookJsonLd: JsonObject | null): string {
  return firstText([
    'h1[itemprop="name"]',
    '[itemprop="name"] h1',
    '.book_name',
    '.book-name',
    '.book-title h1',
    '.book-info h1',
    '.product-info h1',
    'main h1',
    'h1'
  ]) || stringifyJsonLdValue(bookJsonLd?.name) || readMeta('og:title') || 'book'
}

function parseAuthor (bookJsonLd: JsonObject | null): string {
  const selectors = ['[itemprop="author"]', 'a[href*="/contributor/"]', 'a[href*="/author/"]', '.author a', '.book-author a']
  const names = selectors
    .flatMap((selector) => Array.from(document.querySelectorAll<HTMLElement>(selector)))
    .map((el) => cleanText(el.textContent || ''))
    .filter(Boolean)

  return names.length > 0 ? Array.from(new Set(names)).join(', ') : stringifyJsonLdValue(bookJsonLd?.author)
}

function parseIsbn (bookJsonLd: JsonObject | null): string {
  // Prefer EISBN (electronic ISBN) — Readmoo is a digital bookstore.
  // Two-pass: search entire DOM for EISBN first, only fall back to ISBN if not found.
  const fromDetails = readDetailValue(['EISBN', 'eISBN']) || readDetailValue(['ISBN', 'ISBN-13', 'ISBN-10'])
  const fromJson = stringifyJsonLdValue(bookJsonLd?.isbn)
  const value = fromDetails || fromJson
  return value.match(/(?:97[89])?\d[\d-]{8,15}[\dX]/i)?.[0]?.replace(/[^0-9X]/gi, '') || ''
}

function parsePages (bookJsonLd: JsonObject | null): string {
  const numberOfPages = stringifyJsonLdValue(bookJsonLd?.numberOfPages)
  const fromJson = numberOfPages.match(/\b(\d{1,5})\b/)?.[1]
  if (fromJson) return fromJson

  const fromDetails = readDetailValue(['頁數', '紙本書頁數', 'Page Count', 'Pages'])
  return fromDetails.match(/\b(\d{1,5})\b/)?.[1] || ''
}

function parseDescription (bookJsonLd: JsonObject | null): string {
  // Scope to the main book content area (#main_page) to avoid picking up
  // descriptions from related/recommended products elsewhere on the page.
  const scope = document.querySelector('#main_page') || document

  const selectors = ['[itemprop="description"]', '#book-intro', '#intro', '.book-intro', '.description', '.content-intro']
  for (const selector of selectors) {
    const el = scope.querySelector<HTMLElement>(selector)
    const value = el ? parseDescriptionFromHtml(el.innerHTML) : ''
    if (value) return value
  }

  return normalizeDescriptionText(stringifyJsonLdValue(bookJsonLd?.description) || readMeta('description', 'og:description'))
}

function parseCoverUrl (bookJsonLd: JsonObject | null): string {
  const value = stringifyJsonLdValue(bookJsonLd?.image) || document.querySelector<HTMLImageElement>('[itemprop="image"], .book-cover img, .cover img, img[alt*="封面"]')?.src || readMeta('og:image')
  if (value.startsWith('//')) return `${window.location.protocol}${value}`
  return value
}

export function parseReadmooPage (): TransferData {
  const bookJsonLd = readBookJsonLd()

  return {
    title: parseTitle(bookJsonLd),
    originalTitle: '',
    author: parseAuthor(bookJsonLd),
    language: readDetailValue(['語言', 'Language']) || stringifyJsonLdValue(bookJsonLd?.inLanguage),
    publisher: readDetailValue(['出版社', '出版者', 'Publisher']) || stringifyJsonLdValue(bookJsonLd?.publisher),
    isbn: parseIsbn(bookJsonLd),
    pages: parsePages(bookJsonLd),
    pubDate: normalizeDate(readDetailValue(['出版日期', '出版日', '發行日期', 'Publication Date', 'Published']) || stringifyJsonLdValue(bookJsonLd?.datePublished)),
    format: 'digital',
    desc: parseDescription(bookJsonLd),
    rating: '',
    review: '',
    coverUrl: parseCoverUrl(bookJsonLd)
  }
}
